import { create } from "zustand";
import { toast } from "sonner";
import type { EveFundingLedgerRow, EveFundingMode, EveFundingSnapshot } from "@/types/eve";

/**
 * Client funding for platform-wallet orgs — the pot the CS team records
 * deposits into and payouts drain. Reads/writes ride the amy action routes
 * (org comes from the Clerk token; no body params beyond the deposit
 * itself). `canRecord` is the SERVER's allowlist verdict — the UI renders
 * the deposit button from it, and the route re-checks on every write, so a
 * client admin can't self-fund even with the button forced visible.
 */

const EVE_HOST: string = import.meta.env.VITE_AMY_EVE_HOST || "http://127.0.0.1:2000";

interface FundingState {
  /** null = never fetched (or fetch failed) — render nothing. */
  mode: EveFundingMode | null;
  canRecord: boolean;
  funding: EveFundingSnapshot | null;
  ledger: EveFundingLedgerRow[];
  isLoading: boolean;
  isRecording: boolean;
  isEnabling: boolean;
  fetchFunding: (token: string) => Promise<void>;
  recordDeposit: (amount: number, note: string, token: string) => Promise<boolean>;
  /** Staff-only: mark the org Uplodio-managed (platform wallet) so the funds pot appears. */
  enableClientFunding: (token: string) => Promise<boolean>;
}

export const useFundingStore = create<FundingState>((set, get) => ({
  mode: null,
  canRecord: false,
  funding: null,
  ledger: [],
  isLoading: false,
  isRecording: false,
  isEnabling: false,

  fetchFunding: async (token) => {
    set({ isLoading: true });
    try {
      const response = await fetch(`${EVE_HOST}/eve/v1/actions/get-funding`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      const data = (await response.json()) as {
        ok: boolean;
        mode?: EveFundingMode;
        can_record?: boolean;
        funding?: EveFundingSnapshot | null;
        ledger?: EveFundingLedgerRow[];
      };
      if (!data.ok) {
        set({ isLoading: false });
        return;
      }
      set({
        mode: data.mode ?? "none",
        canRecord: data.can_record ?? false,
        funding: data.funding ?? null,
        ledger: data.ledger ?? [],
        isLoading: false,
      });
    } catch {
      // Silent — the frame simply doesn't render until a fetch succeeds.
      set({ isLoading: false });
    }
  },

  recordDeposit: async (amount, note, token) => {
    set({ isRecording: true });
    try {
      const response = await fetch(`${EVE_HOST}/eve/v1/actions/record-funding-deposit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ amount, ...(note.trim() ? { note: note.trim() } : {}) }),
      });
      const data = (await response.json()) as { ok: boolean; error?: string; detail?: string };
      if (!data.ok) {
        toast.error(data.error ?? "Couldn't record the deposit.");
        return false;
      }
      toast.success(data.detail ?? "Deposit recorded.");
      await get().fetchFunding(token);
      return true;
    } catch {
      toast.error("Could not reach Amy — try again in a moment.");
      return false;
    } finally {
      set({ isRecording: false });
    }
  },

  enableClientFunding: async (token) => {
    set({ isEnabling: true });
    try {
      const response = await fetch(`${EVE_HOST}/eve/v1/actions/enable-client-funding`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      const data = (await response.json()) as { ok: boolean; error?: string };
      if (!data.ok) {
        toast.error(data.error ?? "Couldn't enable client funding.");
        return false;
      }
      toast.success("Client funding enabled — record the first deposit to fund it.");
      await get().fetchFunding(token);
      return true;
    } catch {
      toast.error("Could not reach Amy — try again in a moment.");
      return false;
    } finally {
      set({ isEnabling: false });
    }
  },
}));
